import { useState } from 'react';
import { Plus, Trash2, GripVertical, Edit2, Check, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import Button from '../ui/Button';

// Question types
const QUESTION_TYPES = [
  { value: 'text', label: 'Text Answer' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'multiple_choice', label: 'Multiple Choice' }
];

// Single question editor
function QuestionEditor({ question, index, onUpdate, onDelete, onCancel, isNew = false }) {
  const [localQuestion, setLocalQuestion] = useState(question);
  const [newOption, setNewOption] = useState('');

  const handleSave = () => {
    if (!localQuestion?.question?.trim()) return;

    // Validate multiple choice has options
    if (localQuestion?.type === 'multiple_choice' && (!localQuestion?.options || localQuestion?.options?.length < 2)) {
      alert('Multiple choice questions need at least 2 options');
      return;
    }

    onUpdate(localQuestion);
  };

  const handleAddOption = () => {
    if (!newOption?.trim()) return;

    setLocalQuestion(prev => ({
      ...prev,
      options: [...(prev?.options || []), newOption?.trim()]
    }));
    setNewOption('');
  };

  const handleRemoveOption = (optionIndex) => {
    setLocalQuestion(prev => ({
      ...prev,
      options: prev?.options?.filter((_, i) => i !== optionIndex)
    }));
  };

  return (
    <div className="p-4 bg-charcoal-shade-1 rounded-lg border border-charcoal-shade-2 space-y-4">
      {/* Question Text */}
      <div>
        <label className="block text-sm font-medium text-white-shade-1 mb-1">
          Question {index + 1}
        </label>
        <input
          type="text"
          value={localQuestion?.question || ''}
          onChange={(e) => setLocalQuestion(prev => ({ ...prev, question: e?.target?.value }))}
          placeholder="Enter your question..."
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-background placeholder-gray-500 focus:outline-none focus:border-primary-orange"
        />
      </div>

      {/* Question Type */}
      <div className="flex gap-4 items-center">
        <div className="flex-1">
          <label className="block text-sm font-medium text-white-shade-1 mb-1">
            Answer Type
          </label>
          <select
            value={localQuestion?.type || 'text'}
            onChange={(e) => setLocalQuestion(prev => ({
              ...prev,
              type: e?.target?.value,
              options: e?.target?.value === 'multiple_choice' ? (prev?.options || []) : undefined
            }))}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-background focus:outline-none focus:border-primary-orange"
          >
            {QUESTION_TYPES?.map(type => (
              <option key={type?.value} value={type?.value}>
                {type?.label}
              </option>
            ))}
          </select>
        </div>

        {/* Required Toggle */}
        <div className="flex items-center gap-2 pt-6">
          <input
            type="checkbox"
            id={`required-${index}`}
            checked={localQuestion?.required || false}
            onChange={(e) => setLocalQuestion(prev => ({ ...prev, required: e?.target?.checked }))}
            className="w-4 h-4 rounded border-charcoal-shade-2 bg-surface-charcoal text-primary-orange focus:ring-primary-orange focus:ring-offset-dark-charcoal"
          />
          <label htmlFor={`required-${index}`} className="text-sm text-white-shade-1">
            Required
          </label>
        </div>
      </div>

      {/* Multiple Choice Options */}
      {localQuestion?.type === 'multiple_choice' && (
        <div>
          <label className="block text-sm font-medium text-white-shade-1 mb-2">
            Options
          </label>
          <div className="space-y-2">
            {localQuestion?.options?.map((option, optionIndex) => (
              <div key={optionIndex} className="flex items-center gap-2">
                <span className="flex-1 px-3 py-1.5 bg-dark-charcoal rounded text-sm text-white-shade-1">
                  {option}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveOption(optionIndex)}
                  className="p-1 text-red-400 hover:text-red-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}

            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newOption}
                onChange={(e) => setNewOption(e?.target?.value)}
                onKeyDown={(e) => e?.key === 'Enter' && (e?.preventDefault(), handleAddOption())}
                placeholder="Add option..."
                className="flex-1 px-3 py-1.5 bg-white border border-gray-300 rounded text-sm text-background placeholder-gray-500 focus:outline-none focus:border-primary-orange"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddOption}
                disabled={!newOption?.trim()}
              >
                Add
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2 border-t border-charcoal-shade-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={handleSave}
          disabled={!localQuestion?.question?.trim()}
        >
          {isNew ? 'Add Question' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

// Question display card
function QuestionCard({ question, index, onEdit, onDelete }) {
  const typeLabel = QUESTION_TYPES?.find(t => t?.value === question?.type)?.label || 'Text';

  return (
    <div className="p-4 bg-surface-charcoal rounded-lg border border-charcoal-shade-2 group">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 pt-1 text-white-shade-2 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity">
          <GripVertical className="w-4 h-4" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-text-primary font-medium">
                Q{index + 1}: {question?.question}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs px-2 py-0.5 bg-dark-charcoal rounded text-white-shade-2">
                  {typeLabel}
                </span>
                {question?.required && (
                  <span className="text-xs px-2 py-0.5 bg-primary-orange/20 text-primary-orange rounded">
                    Required
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={onEdit}
                className="p-1.5 text-white-shade-2 hover:text-text-primary hover:bg-charcoal-shade-1 rounded"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={onDelete}
                className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Show options for multiple choice */}
          {question?.type === 'multiple_choice' && question?.options?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {question?.options?.map((option, i) => (
                <span
                  key={i}
                  className="text-xs px-2 py-1 bg-dark-charcoal rounded text-white-shade-1"
                >
                  {option}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Main QuestionnaireBuilder component
export default function QuestionnaireBuilder({
  value = [],
  onChange,
  className,
  maxQuestions = 20,
  disabled = false
}) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [isAdding, setIsAdding] = useState(false);

  const questions = value || [];

  const handleAddQuestion = (newQuestion) => {
    onChange?.([...questions, { ...newQuestion, id: Date.now() }]);
    setIsAdding(false);
  };

  const handleUpdateQuestion = (index, updatedQuestion) => {
    const updated = [...questions];
    updated[index] = updatedQuestion;
    onChange?.(updated);
    setEditingIndex(null);
  };

  const handleDeleteQuestion = (index) => {
    const updated = questions?.filter((_, i) => i !== index);
    onChange?.(updated);
  };

  const canAddMore = questions?.length < maxQuestions;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Existing Questions */}
      {questions?.map((question, index) => (
        <div key={question?.id || index}>
          {editingIndex === index ? (
            <QuestionEditor
              question={question}
              index={index}
              onUpdate={(q) => handleUpdateQuestion(index, q)}
              onDelete={() => handleDeleteQuestion(index)}
              onCancel={() => setEditingIndex(null)}
            />
          ) : (
            <QuestionCard
              question={question}
              index={index}
              onEdit={() => setEditingIndex(index)}
              onDelete={() => handleDeleteQuestion(index)}
            />
          )}
        </div>
      ))}

      {/* Add New Question */}
      {isAdding ? (
        <QuestionEditor
          question={{ question: '', type: 'text', required: false }}
          index={questions?.length}
          onUpdate={handleAddQuestion}
          onCancel={() => setIsAdding(false)}
          isNew
        />
      ) : (
        canAddMore && !disabled && (
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            className="w-full py-3 border-2 border-dashed border-charcoal-shade-2 rounded-lg text-white-shade-2 hover:border-primary-orange hover:text-primary-orange transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Question
          </button>
        )
      )}

      {/* Empty State */}
      {questions?.length === 0 && !isAdding && (
        <p className="text-center text-white-shade-2 text-sm py-4">
          No questions added yet. Click "Add Question" to create a questionnaire.
        </p>
      )}

      {/* Max Questions Warning */}
      {!canAddMore && (
        <p className="text-xs text-white-shade-2 text-center">
          Maximum {maxQuestions} questions reached
        </p>
      )}
    </div>
  );
}
